import './Upload.css';
import { useState, useRef, useEffect, useCallback } from 'react';
import SERVER_URL from '../../../../env.js';
import { papaParseCSV, getParsedReturnsObjects, makeKey } from '../../../../Utils/FileUtil.js';
import Cookies from 'js-cookie';

const STATUS_OPTIONS = ['Open', 'Filed', 'Pending', 'Awaiting Documents'];
const EMPTY_MANUAL_FORM = {
    year: '',
    status: 'Open',
    cash: '',
    e_transfer_or_cheque: '',
    invoice_number: '',
    last_contact_date: '',
    notes: '',
};
function Upload({ toggleNav, closeCreate, setReturns }) {
    // --- CSV State ---
    const [parsedReturns, setParsedReturns] = useState([]);
    const [chosenFile, setChosenFile] = useState(null);
    const fileInputRef = useRef(null);
    // --- Shared ---
    const [activeTab, setActiveTab] = useState('manual');
    const [updateMessage, setUpdateMessage] = useState('');
    const [isError, setIsError] = useState(false);
    // --- Manual State ---
    const [clients, setClients] = useState([]);
    const [clientSearch, setClientSearch] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [showClientModal, setShowClientModal] = useState(false);
    const [manualForm, setManualForm] = useState(EMPTY_MANUAL_FORM);
    const [feeRows, setFeeRows] = useState([{ feeType: '', amount: '' }]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [returnsCountThisYear, setReturnsCountThisYear] = useState(null);
    const fetchClients = useCallback(() => {
        fetch(SERVER_URL + '/clients', {
            headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
        })
            .then(r => r.json())
            .then(data => setClients(data.clients || []))
            .catch(err => console.error('Failed to fetch clients:', err));
    }, []);
    useEffect(() => {
        if (activeTab === 'manual') fetchClients();
    }, [activeTab, fetchClients]);

    // Fetch the count of returns created this year for invoice number generation
    useEffect(() => {
        fetch(SERVER_URL + '/returns/count-this-year', {
            headers: { 'Authorization': 'Bearer ' + Cookies.get('accessToken') },
        })
            .then(r => r.json())
            .then(data => setReturnsCountThisYear(data.count ?? 0))
            .catch(err => console.error('Failed to fetch returns count:', err));
    }, []);

    // Auto-generate invoice number: PT-YYYYNNN
    const currentYear = new Date().getFullYear();
    const nextNumber = (returnsCountThisYear !== null ? returnsCountThisYear : 0) + 1;
    const autoInvoiceNumber = `PT-${currentYear}${String(nextNumber).padStart(3, '0')}`;
    const setMessage = (msg, error = false) => {
        setUpdateMessage(msg);
        setIsError(error);
    };
    // ── CSV handlers ────────────────────────────────────────────────────────────
    const handleReturnsFile = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        setChosenFile(file);
        const ext = file.name.split('.').pop().toLowerCase();
        if (!ext.includes('csv') && (!file.type || !file.type.includes('csv'))) {
            alert('Please upload a .csv file type.');
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            if (text) {
                const csvData = papaParseCSV(text);
                if (csvData.error) { setMessage(csvData.error, true); return; }
                const parseResult = getParsedReturnsObjects(csvData);
                if (parseResult.error) { setMessage(parseResult.error, true); return; }
                setParsedReturns(parseResult);
            }
        };
        reader.readAsText(file);
    };
    const handleFileUpload = () => {
        if (!chosenFile) return;
        const ext = chosenFile.name.split('.').pop().toLowerCase();
        if (!ext.includes('csv') && (!chosenFile.type || !chosenFile.type.includes('csv'))) {
            setMessage('Please upload a .csv file type.', true);
            return;
        }
        if (!parsedReturns || parsedReturns.length === 0) {
            setMessage('No returns found in the file.', true);
            return;
        }
        const { clients: csvClients, returns: csvReturns, duplicates: csvDuplicates } = parsedReturns;
        if (csvDuplicates && csvDuplicates.length > 0) {
            const clientNames = csvDuplicates.map(r => `(${r.clientID}) for the year ${r.year}`).join(', ');
            setMessage(`Duplicate returns found for ${clientNames}.`, true);
            return;
        }
        fetch(SERVER_URL + '/bulk/bulk-upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + Cookies.get('accessToken'),
            },
            body: JSON.stringify({ clients: csvClients, returns: csvReturns }),
        })
            .then(async (response) => {
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || errData.message || 'HTTP error ' + response.status);
                }
                return response.json();
            })
            .then((data) => {
                setChosenFile(null);
                setMessage(
                    'Successfully Added ' +
                    (data.clients?.length > 0 ? data.clients.length + ' Clients, ' : '') +
                    (data.returns?.length > 0 ? data.returns.length + ' Returns' : '')

                );

                if (data.clients?.length > 0) {
                    setClients(prev => [...prev, ...data.clients]);
                }

                if (data.returns?.length > 0) {
                    setReturns(prev => [...prev, ...data.returns]);
                }

                if (closeCreate) {
                    closeCreate();
                }
            })
            .catch((error) => {
                setMessage('Error uploading file. ' + error.message, true);
            });
    };
    // ── Manual handlers ─────────────────────────────────────────────────────────
    const filteredClients = clients.filter(c =>
        c.client_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
        (c.emails || '').toLowerCase().includes(clientSearch.toLowerCase())
    );
    const handleSelectClient = (client) => {
        setSelectedClient(client);
        setShowClientModal(false);
        setClientSearch('');
    };
    const handleManualFormChange = (e) => {
        const { name, value } = e.target;
        setManualForm(prev => ({ ...prev, [name]: value }));
    };
    const handleFeeRowChange = (index, field, value) => {
        setFeeRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
    };
    const addFeeRow = () => setFeeRows(prev => [...prev, { feeType: '', amount: '' }]);
    const removeFeeRow = (index) => setFeeRows(prev => prev.filter((_, i) => i !== index));
    const handleManualSubmit = async () => {
        if (!selectedClient) { setMessage('Please select a client.', true); return; }
        if (!manualForm.year) { setMessage('Year is required.', true); return; }
        setIsSubmitting(true);
        setUpdateMessage('');
        let feeData = null;
        let returnData = null;
        try {
            const returnRes = await fetch(SERVER_URL + '/returns', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                },
                body: JSON.stringify({
                    client_id: selectedClient.id,
                    year: manualForm.year,
                    cash: parseFloat(manualForm.cash) || 0,
                    e_transfer_or_cheque: parseFloat(manualForm.e_transfer_or_cheque) || 0,
                    outstanding_balance: (
                        feeRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0) -
                        (parseFloat(manualForm.cash) || 0) -
                        (parseFloat(manualForm.e_transfer_or_cheque) || 0)
                    ),
                    invoice_number: autoInvoiceNumber,
                    last_contact_date: manualForm.last_contact_date || null,
                    notes: manualForm.notes || '',
                    status: manualForm.status || 'Open',
                }),
            });
            if (!returnRes.ok) {
                const errData = await returnRes.json().catch(() => ({}));
                throw new Error(errData.error || 'HTTP error ' + returnRes.status);
            }
            returnData = await returnRes.json();
            const returnId = returnData.return.id;
            const validFees = feeRows.filter(r => r.feeType.trim() && r.amount !== '');
            if (validFees.length > 0) {
                const feesRes = await fetch(SERVER_URL + '/fees/bulk-upload', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + Cookies.get('accessToken'),
                    },
                    body: JSON.stringify({
                        fees: validFees.map(r => ({
                            client_id: selectedClient.id,
                            return_id: returnId,
                            fee_type: r.feeType.trim(),
                            amount: parseFloat(r.amount),
                        })),
                    }),
                });
                if (!feesRes.ok) {
                    const errData = await feesRes.json().catch(() => ({}));
                    throw new Error(errData.error || 'HTTP error ' + feesRes.status);
                }
                feeData = await feesRes.json();
            }


            const newReturn = {
                ...returnData.return,
                client_id: returnData.return.client_id || selectedClient.id,
                client_name: returnData.return.client_name || selectedClient.client_name,
                phone: returnData.return.phone || selectedClient.phone,
                email: returnData.return.email || selectedClient.emails,
                cra: returnData.return.cra || selectedClient.my_cra,
                auth_rep: returnData.return.auth_rep || selectedClient.auth_rep,
                status: returnData.return.status || manualForm.status || 'Open',
                fees: validFees.map(r => ({
                    fee_type: r.feeType.trim(),
                    amount: parseFloat(r.amount) || 0
                }))
            };

            if (setReturns) {
                setReturns(prev => [newReturn, ...prev]);
            }

            setMessage('Return for ' + selectedClient.client_name + ' (' + manualForm.year + ') uploaded successfully.');
            setReturnsCountThisYear(prev => (prev ?? 0) + 1);
            setManualForm(EMPTY_MANUAL_FORM);
            setFeeRows([{ feeType: '', amount: '' }]);
            setSelectedClient(null);

            if (closeCreate) {
                closeCreate();
            }
        } catch (err) {
            setMessage('Error: ' + err.message, true);
        } finally {
            setIsSubmitting(false);
        }
    };
    const handleTabSwitch = (tab) => {
        setActiveTab(tab);
        setUpdateMessage('');
        setIsError(false);
    };
    return (
        <div className="Upload">
            {/* Tab Switcher */}
            <div className="upload-tabs">
                <button
                    className={'upload-tab' + (activeTab === 'manual' ? ' active' : '')}
                    onClick={() => handleTabSwitch('manual')}
                >
                    Manual Entry
                </button>
                <button
                    className={'upload-tab' + (activeTab === 'csv' ? ' active' : '')}
                    onClick={() => handleTabSwitch('csv')}
                >
                    CSV Upload
                </button>
            </div>
            {/* Status Message */}
            {updateMessage && (
                <p className={'update-message' + (isError ? ' update-message--error' : '')}>{updateMessage}</p>
            )}
            {/* ── CSV Tab ── */}
            {activeTab === 'csv' && (
                <div className="upload-container">
                    <div className="upload-question-container">
                        <input ref={fileInputRef} type="file" onChange={handleReturnsFile} style={{ display: 'none' }} accept=".csv" />
                        <div className="file-upload" onClick={() => fileInputRef.current.click()}>
                            {chosenFile?.name || 'Choose CSV File'}
                        </div>
                    </div>
                    <button className="upload-submit" onClick={handleFileUpload}>Upload Returns</button>
                </div>
            )}
            {/* ── Manual Tab ── */}
            {activeTab === 'manual' && (
                <div className="manual-entry-container">
                    {/* Client Selection */}
                    <div className="manual-section">
                        <h3 className="manual-section-title">Client <span className="required">*</span></h3>
                        <div
                            className={'client-picker-display' + (selectedClient ? ' selected' : '')}
                            onClick={() => setShowClientModal(true)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => e.key === 'Enter' && setShowClientModal(true)}
                        >
                            {selectedClient ? (
                                <>
                                    <span className="client-picker-name">{selectedClient.client_name}</span>
                                    <span className="client-picker-email">{selectedClient.emails}</span>
                                </>
                            ) : (
                                <span className="client-picker-placeholder">Select a client&hellip;</span>
                            )}
                            <span className="client-picker-chevron">&#8964;</span>
                        </div>
                    </div>
                    {/* Return Details */}
                    <div className="manual-section">
                        <h3 className="manual-section-title">Return Details</h3>
                        <div className="manual-form-grid">
                            <div className="form-group">
                                <label htmlFor="manual-year">Year <span className="required">*</span></label>
                                <input
                                    id="manual-year"
                                    type="number"
                                    min={1900}
                                    name="year"
                                    value={manualForm.year}
                                    onChange={handleManualFormChange}
                                    placeholder="2024"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="manual-invoice">Invoice Number</label>
                                <div className="return-calculated-field" id="manual-invoice">
                                    {returnsCountThisYear !== null ? autoInvoiceNumber : 'Loading...'}
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="manual-status">Status</label>
                                <select
                                    id="manual-status"
                                    name="status"
                                    value={manualForm.status}
                                    onChange={handleManualFormChange}
                                >
                                    {STATUS_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="manual-cash">Cash ($)</label>
                                <input
                                    id="manual-cash"
                                    type="number"
                                    name="cash"
                                    value={manualForm.cash}
                                    onChange={handleManualFormChange}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="manual-etransfer">E-Transfer / Cheque ($)</label>
                                <input
                                    id="manual-etransfer"
                                    type="number"
                                    name="e_transfer_or_cheque"
                                    value={manualForm.e_transfer_or_cheque}
                                    onChange={handleManualFormChange}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                            <div className="form-group">
                                <label>Outstanding Balance ($)</label>
                                <div className="return-calculated-field">
                                    ${(
                                        feeRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0) -
                                        (parseFloat(manualForm.cash) || 0) -
                                        (parseFloat(manualForm.e_transfer_or_cheque) || 0)
                                    ).toFixed(2)}
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="manual-contact-date">Last Contact Date</label>
                                <input
                                    id="manual-contact-date"
                                    type="date"
                                    name="last_contact_date"
                                    value={manualForm.last_contact_date}
                                    onChange={handleManualFormChange}
                                />
                            </div>
                            <div className="form-group form-group--full">
                                <label htmlFor="manual-notes">Notes / Comments</label>
                                <textarea
                                    id="manual-notes"
                                    name="notes"
                                    value={manualForm.notes}
                                    onChange={handleManualFormChange}
                                    placeholder="Any additional notes..."
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>
                    {/* Fees */}
                    <div className="manual-section">
                        <div className="manual-section-header">
                            <h3 className="manual-section-title">Fees</h3>
                            <button className="add-fee-btn" onClick={addFeeRow}>+ Add Fee</button>
                        </div>
                        <div className="fee-rows">
                            {feeRows.map((row, i) => (
                                <div key={i} className="fee-row">
                                    <input
                                        type="text"
                                        placeholder="Fee type (T1, HST, etc.)"
                                        value={row.feeType}
                                        onChange={e => handleFeeRowChange(i, 'feeType', e.target.value)}
                                    />
                                    <input
                                        type="number"
                                        placeholder="Amount"
                                        value={row.amount}
                                        step="0.01"
                                        min="0"
                                        onChange={e => handleFeeRowChange(i, 'amount', e.target.value)}
                                    />
                                    {feeRows.length > 1 && (
                                        <button
                                            className="remove-fee-btn"
                                            onClick={() => removeFeeRow(i)}
                                            aria-label="Remove fee"
                                        >&#x2715;</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="manual-actions">
                        <button
                            className="upload-submit"
                            onClick={handleManualSubmit}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Creating...' : 'Create Return'}
                        </button>
                    </div>
                </div>
            )}
            {/* ── Client Picker Modal ── */}
            {showClientModal && (
                <div className="modal-overlay" onClick={() => setShowClientModal(false)}>
                    <div className="client-modal" onClick={e => e.stopPropagation()}>
                        <div className="client-modal-header">
                            <h3>Select Client</h3>
                            <button
                                className="modal-close"
                                onClick={() => setShowClientModal(false)}
                                aria-label="Close"
                            >&#x2715;</button>
                        </div>
                        <input
                            className="returns-client-search-input"
                            type="text"
                            placeholder="Search by name or email..."
                            value={clientSearch}
                            onChange={e => setClientSearch(e.target.value)}
                            autoFocus
                        />
                        <div className="returns-client-list">
                            {filteredClients.length === 0 ? (
                                <p className="client-list-empty">No clients found.</p>
                            ) : (
                                filteredClients.map(c => (
                                    <div
                                        key={c.id}
                                        className="client-list-item"
                                        onClick={() => handleSelectClient(c)}
                                    >
                                        <span className="client-list-name">{c.client_name}</span>
                                        <span className="client-list-email">{c.emails}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
export default Upload;